import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send } from "lucide-react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { useContent } from "@/lib/content";
import { PageHead } from "@/pages/admin/AdminLayout";
import { useUnsavedGuard } from "@/pages/admin/UserDetail";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { usePageTitle } from "@/components/layout/AppLayout";
import { CtaField, FieldActions, TextField, validateField } from "@/components/admin/ContentFields";
import { ImageCard } from "@/components/admin/ContentImages";
import { FaqField, validateFaq } from "@/components/admin/FaqField";
import { SeoPanel } from "@/components/admin/SeoPanel";

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const groupBy = (list, k) => list.reduce((m, x) => ({ ...m, [x[k]]: [...(m[x[k]] || []), x] }), {});

const Field = ({ f, ctx }) => {
    const { draft, saved, setDraft, errors, status, save, restore, readOnly, pages, placeholders } = ctx;
    const value = draft[f.key];
    const Comp = f.kind === "cta" ? CtaField : f.kind === "faq" ? FaqField : TextField;
    return (
        <div className="space-y-2 border-t border-edi-border pt-4 first:border-0 first:pt-0" data-testid={`cms-field-${f.key.replace(/[._]/g, "-")}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-edi-muted break-words">Dove appare: {pages[f.page]} › {f.section}</p>
            <Comp f={f} value={value} onChange={(v) => { setDraft((d) => ({ ...d, [f.key]: v })); ctx.clearError(f.key); }} errors={errors[f.key]} readOnly={readOnly}
                placeholders={f.kind === "email_body" ? placeholders : null} />
            <FieldActions f={f} readOnly={readOnly} dirty={!same(value, saved[f.key])} isDefault={same(saved[f.key], f.default)} pageName={pages[f.page]}
                onSave={() => save(f)} onRestore={() => restore(f)} status={status[f.key]} />
        </div>
    );
};

const EmailTest = ({ tpl, ctx }) => {
    const [msg, setMsg] = useState(null);
    const send = async () => {
        const body = Object.fromEntries(["subject", "heading", "body", "button"].map((k) => [k, ctx.draft[`email.${tpl}.${k}`]]));
        setMsg({ kind: "info", text: "Invio in corso…" });
        try { const { data } = await api.post(`/admin/content/email-test/${tpl}`, body); setMsg({ kind: "success", text: data.message }); }
        catch (err) { setMsg({ kind: "error", text: errorMessage(err) }); }
    };
    if (ctx.readOnly) return null;
    return (
        <div className="flex flex-wrap items-center gap-3 border-t border-edi-border pt-4">
            <button type="button" className="btn-secondary py-2" onClick={send} data-testid={`cms-email-test-${tpl}`}><Send aria-hidden="true" size={16} /> Invia email di prova</button>
            <span className="text-sm text-edi-muted">Usa i testi attuali, anche non pubblicati, con dati di esempio.</span>
            {msg && <p role="status" className="text-sm font-semibold" data-testid={`cms-email-test-status-${tpl}`}>{msg.text}</p>}
        </div>
    );
};

const Section = ({ name, fields, ctx, page }) => {
    const id = `cms-sec-${page}-${name}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const tpl = page === "email" && fields[0].key.split(".")[1];
    return (
        <section className="edi-card p-5 sm:p-6 space-y-4" aria-labelledby={id} data-testid={id}>
            <h3 id={id} className="text-xl font-bold">{page === "email" ? `Email «${name}»` : name}</h3>
            {fields.map((f) => <Field key={f.key} f={f} ctx={ctx} />)}
            {tpl && <EmailTest tpl={tpl} ctx={ctx} />}
        </section>
    );
};

const usePageParam = (keys) => {
    const [params, setParams] = useSearchParams();
    const cur = keys.includes(params.get("pagina")) ? params.get("pagina") : keys[0];
    return [cur, (p) => setParams({ pagina: p }, { replace: true })];
};

export default function ContentEditor() {
    usePageTitle("Contenuti del sito");
    const { reload } = useContent();
    const [data, setData] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [draft, setDraft] = useState({});
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({});
    const load = () => api.get("/admin/content").then(({ data: d }) => { setData(d); setDraft(Object.fromEntries(d.fields.map((f) => [f.key, f.value]))); }).catch((e) => setLoadErr(errorMessage(e)));
    useEffect(() => { load(); }, []);
    const saved = useMemo(() => Object.fromEntries((data?.fields || []).map((f) => [f.key, f.value])), [data]);
    const dirty = data ? data.fields.some((f) => !same(draft[f.key], saved[f.key])) : false;
    useUnsavedGuard(dirty);
    const [page, setPage] = usePageParam([...Object.keys(data?.pages || { home: 1 }), "immagini", "seo"]);
    if (loadErr) return <Notice kind="error" role="alert">{loadErr}</Notice>;
    if (!data) return <Loading />;

    const done = (f, value, text) => {
        setData((d) => ({ ...d, fields: d.fields.map((x) => (x.key === f.key ? { ...x, value } : x)) }));
        setDraft((d) => ({ ...d, [f.key]: value }));
        setErrors((e) => ({ ...e, [f.key]: null }));
        setStatus((s) => ({ ...s, [f.key]: { kind: "success", text } }));
        reload();
    };
    const fail = (f, err) => {
        const fe = fieldErrors(err);
        setErrors((e) => ({ ...e, [f.key]: fe || null }));
        setStatus((s) => ({ ...s, [f.key]: fe ? null : { kind: "error", text: errorMessage(err) } }));
    };
    const save = async (f) => {
        const v = draft[f.key];
        const e = f.kind === "faq" ? validateFaq(v) : validateField(f, v);
        setErrors((x) => ({ ...x, [f.key]: e }));
        setStatus((s) => ({ ...s, [f.key]: null }));
        if (e) {
            const fid = f.kind === "faq" ? `cms-faq-${Object.keys(e)[0].replace(".", "-")}` : `cms-${f.key.replace(/[._]/g, "-")}${f.kind === "cta" ? (e.text ? "-text" : "-url") : ""}`;
            document.getElementById(fid)?.focus();
            return;
        }
        try { const { data: r } = await api.put(`/admin/content/${f.key}`, { value: v }); done(f, r.value, r.message); } catch (err) { fail(f, err); }
    };
    const restore = async (f) => {
        try { const { data: r } = await api.delete(`/admin/content/${f.key}`); done(f, r.value, r.message); } catch (err) { fail(f, err); }
    };
    const readOnly = !data.can_edit;
    const ctx = { draft, saved, setDraft, errors, status, save, restore, readOnly, pages: data.pages, placeholders: data.placeholders,
        clearError: (k) => { if (errors[k]) setErrors((e) => ({ ...e, [k]: null })); } };
    const tabs = [...Object.entries(data.pages), ["immagini", "Immagini"], ["seo", "SEO e indicizzazione"]];
    const sections = groupBy(data.fields.filter((f) => f.page === page), "section");

    return (
        <>
            <PageHead title="Contenuti del sito" />
            {readOnly && <div className="mb-4"><Notice kind="info" testId="cms-readonly-notice">Sola visualizzazione: non hai il permesso di modificare i contenuti.</Notice></div>}
            <div className="grid gap-6 grid-cols-[minmax(0,1fr)] lg:grid-cols-[220px_minmax(0,1fr)] items-start">
                <nav aria-label="Pagine del sito" className="min-w-0 lg:sticky lg:top-[calc(var(--edi-header-h,72px)+16px)]" data-testid="cms-page-nav">
                    <ul className="flex lg:flex-col gap-1 overflow-x-auto pb-1">
                        {tabs.map(([k, l]) => (
                            <li key={k} className="shrink-0">
                                <button type="button" aria-current={page === k ? "page" : undefined} onClick={() => setPage(k)} data-testid={`cms-tab-${k}`}
                                    className={`w-full text-left rounded-lg px-4 py-2 font-semibold ${page === k ? "edi-active" : "hover:bg-edi-tertiary"}`}>{l}</button>
                            </li>
                        ))}
                    </ul>
                </nav>
                <div className="min-w-0 space-y-6" data-testid={`cms-panel-${page}`}>
                    <h2 className="text-2xl font-extrabold">{page === "immagini" ? "Immagini" : page === "seo" ? "SEO e indicizzazione" : data.pages[page]}</h2>
                    <p className="text-edi-muted -mt-4">{page === "seo" ? "Titoli, descrizioni, dati strutturati e file per motori di ricerca e assistenti AI. Le modifiche ai tag sono pubblicate subito." : page === "immagini" ? "Le immagini vengono pubblicate subito dopo il caricamento." : "Testo semplice con a capo. Ogni modifica viene pubblicata subito con «Pubblica»."}</p>
                    {page === "seo" ? <SeoPanel readOnly={readOnly} /> : page === "immagini"
                        ? <div className="grid gap-6 xl:grid-cols-2">{Object.entries(data.images).map(([s, img]) => <ImageCard key={s} slot={s} img={img} readOnly={readOnly} onDone={() => { load(); reload(); }} />)}</div>
                        : Object.entries(sections).map(([name, fs]) => <Section key={name} name={name} fields={fs} ctx={ctx} page={page} />)}
                </div>
            </div>
        </>
    );
}
